import {
	HouseOwner,
	Lien,
	Property,
	PropertyTypes,
	SpecialConstraint
} from "@ploy-lib/types";
import { Entity } from "@rest-hooks/endpoint";

export class HouseOwnerEntity extends Entity implements HouseOwner {
	constructor() {
		super();
		this.houseOwnerId = 0;
		this.uniquePropertyId = "";
		this.applicationPropertyId = 0;
		this.dateOfBirth = new Date();
		this.organizationNumber = "";
		this.personalNumber = "";
		this.name = "";
		this.email = "";
		this.phone = "";
		this.isNorwegianCitizen = false;
		this.owningFractionNumerator = 0;
		this.owningFractionDenominator = 0;
		this.isResident = false;
		this.displayValue = "";
	}

	readonly houseOwnerId: number = 0;
	readonly uniquePropertyId: string = "";
	readonly applicationPropertyId?: number = 0;
	readonly dateOfBirth: Date;
	readonly organizationNumber: string = "";
	readonly personalNumber?: string = "";
	readonly name?: string = "";
	readonly email?: string = "";
	readonly phone?: string = "";
	readonly isNorwegianCitizen: boolean = false;
	readonly owningFractionNumerator?: number = 0;
	readonly owningFractionDenominator: number = 0;
	readonly isResident: boolean = false;

	displayValue: string = "";

	pk(parent?: any, key?: string) {
		return this.houseOwnerId.toString();
	}
}

export class LienEntity extends Entity implements Lien {
	constructor() {
		super();
		this.lienId = 0;
		this.uniquePropertyId = "";
		this.creditorOrgNo = "";
		this.creditorName = "";
		this.priority = "";
		this.yieldPriority = false;
		this.writtenDownValue = 0;
		this.documentId = "";
		this.addedManually = false;
		this.registrationDate = new Date();
		this.lienType = "";
		this.infoAsText = "";
		this.value = 0;
		this.reuseLien = false;
	}

	readonly lienId: number = 0;
	readonly uniquePropertyId: string = "";
	readonly creditorOrgNo?: string = "";
	readonly creditorName?: string = "";
	readonly priority?: string = "";
	readonly yieldPriority?: boolean = false;
	readonly writtenDownValue?: number = 0;
	readonly addedManually?: boolean | undefined = false;
	readonly documentId?: string = "";
	readonly registrationDate?: Date = new Date();
	readonly lienType?: string = "";
	readonly infoAsText?: string = "";
	readonly value?: number = 0;
	readonly reuseLien?: boolean = false;

	pk(parent?: any, key?: string) {
		return this.lienId?.toString() ?? "";
	}
}

export class SpecialConstraintEntity
	extends Entity
	implements SpecialConstraint
{
	constructor() {
		super();
		this.specialConstraintId = 0;
		this.documentId = "";
		this.registrationDate = new Date();
		this.name = "";
		this.warningLevel = undefined;
		this.isJudicialRegistered = undefined;
	}
	specialConstraintId: number;
	documentId: string;
	registrationDate: Date;
	name: string;
	warningLevel?: number | null | undefined;
	isJudicialRegistered?: boolean | null | undefined;

	pk(parent?: any, key?: string) {
		return this.specialConstraintId?.toString();
	}
}

export class ApplicationPropertyEntity extends Entity implements Property {
	constructor() {
		super();
		this.applicationPropertyId = 0;
		this.applicationId = 0;
		this.propertyInformation = "";
		this.uniquePropertyId = "";
		this.type = "Unknown";
		this.cadastralNumber = "";
		this.streetName = undefined;
		this.addressNumber = undefined;
		this.addressLetter = undefined;
		this.municipalityNumber = undefined;
		this.postalCode = undefined;
		this.postalPlace = undefined;
		this.unitNumber = undefined;
		this.leaseholdUnitNumber = undefined;
		this.refinance = undefined;
		this.valueEnteredByCustomer = undefined;
		this.mainCustomerResidence = undefined;
		this.estimatedValue = undefined;
		this.pledgeValue = undefined;
		this.cadastralUnitNumber = undefined;
		this.housingCooperativeOrgNumber = undefined;
		this.housingCooperativeOrgName = undefined;
		this.shareNumber = undefined;
		this.housingCooperativeName = undefined;
		this.jointDebt = undefined;
		this.businessManager = undefined;
		this.apartmentNumber = undefined;
		this.utilizedValue = undefined;
		this.utilizedSecurityValue = undefined;
		this.freeText = undefined;
		this.owners = [new HouseOwnerEntity()];
		this.liens = [new LienEntity()];
		this.newLiens = [new LienEntity()];
		this.specialConstraints = [new SpecialConstraintEntity()];
		this.eAppraisalReference = undefined;
		this.eAppraisalDate = undefined;
		this.deleted = undefined;
		this.priority = undefined;
	}

	pk(parent?: any, key?: string | undefined): string | undefined {
		return this.propertyInformation?.toString() || "";
	}

	readonly applicationPropertyId: number;
	readonly applicationId: number;
	readonly propertyInformation: string;
	readonly uniquePropertyId: string;
	readonly type: PropertyTypes;
	readonly cadastralNumber: string;
	readonly streetName?: string | undefined;
	readonly addressNumber?: number | undefined;
	readonly addressLetter?: string | undefined;
	readonly municipalityNumber?: number | undefined;
	readonly postalCode?: string | undefined;
	readonly postalPlace?: string | undefined;
	readonly unitNumber?: number | undefined;
	readonly leaseholdUnitNumber?: number | undefined;
	readonly refinance?: boolean | undefined;
	readonly valueEnteredByCustomer?: number | undefined;
	readonly mainCustomerResidence?: boolean | undefined;
	readonly estimatedValue?: number | undefined;
	readonly pledgeValue?: number | undefined;
	readonly cadastralUnitNumber?: number | undefined;
	readonly housingCooperativeOrgNumber?: string | undefined;
	readonly housingCooperativeOrgName?: string | undefined;
	readonly shareNumber?: number | undefined;
	readonly housingCooperativeName?: string | undefined;
	readonly jointDebt?: number | undefined;
	readonly businessManager?: string | undefined;
	readonly apartmentNumber?: string | undefined;
	readonly utilizedValue?: number | undefined;
	readonly utilizedSecurityValue?: number | undefined;
	readonly freeText?: string | undefined;
	readonly owners?: HouseOwnerEntity[] | undefined;
	readonly liens?: LienEntity[] | undefined;
	readonly newLiens?: LienEntity[] | undefined;
	readonly specialConstraints?: SpecialConstraintEntity[] | undefined;
	readonly eAppraisalReference?: string | undefined;
	readonly eAppraisalDate?: Date | undefined;
	readonly deleted?: boolean | undefined;
	readonly priority?: number;
}
